/* eslint-disable no-use-before-define */
import React, { useMemo, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import referencesService, { Player } from '../../services/references';
import useReferenceList from './useReferenceList';
import Progress from './Progress';
import { useSnackbar } from 'notistack';
import { TPlayer } from '../Sessions/Lineup/SessionLineupsPlayer';
import { FormControlLabel, Checkbox } from '@material-ui/core';

interface Props {
  value: string | null | undefined;
  keeper: boolean;
  label?: string;
  team?: string;
  add?: boolean;
  exclude?: Array<String>;
  displayNumber?: boolean;
  filterOptionsOnTeam?: boolean;
  onChange: (Player: Player | null) => void;
}

type PlayerOptionType = Player & { inputValue?: string };

export const displayName = (player: TPlayer | null, displayNumber: boolean = false) => {
  if (!player) return 'Unknown';
  let ret = '';
  if(displayNumber) {
    ret += player.lastKnownNumber + ". ";
  }
  if (player.lastName) {
    ret += player.lastName;
  }
  if (player.firstName) {
    if (ret !== '') ret += ', ';
    ret += player.firstName;
  }
  if (player.knownAs) {
    if (ret === '') {
      ret = player.knownAs;
    } else {
      ret += ' (' + player.knownAs + ')';
    }
  }
  return ret;
};

const filter = createFilterOptions<PlayerOptionType>({ stringify: displayName });

const playerSort = (team?: string) => (a: Player, b: Player) => {
  if (a.lastKnownTeam && b.lastKnownTeam && a.lastKnownTeam.id === b.lastKnownTeam.id) {
    //Players of the same team, sort by name
    const nameA = displayName(a);
    const nameB = displayName(b);
    return nameA.localeCompare(nameB);
  } else {
    const teamA = a?.lastKnownTeam?.name || 'Unknown';
    const teamB = b?.lastKnownTeam?.name || 'Unknown';
    if (a?.lastKnownTeam?.id === team) return -1;
    if (b?.lastKnownTeam?.id === team) return 1;
    return teamA.localeCompare(teamB);
  }
};

const dialogValueFromString = (input: string, keeper: boolean) => {
  const comma = input.indexOf(',');
  let firstName = '';
  let lastName = '';
  if (comma !== -1) {
    // Lastname, firstname
    lastName = input.substring(0, comma).trim();
    firstName = input.substring(comma + 1).trim();
  } else {
    const space = input.indexOf(' ');
    if (space !== -1) {
      // Firstname lastname (assume only one space, not much more we can do)
      firstName = input.substring(0, space).trim();
      lastName = input.substring(space + 1).trim();
    } else {
      // Lastname
      lastName = input.trim();
    }
  }

  return {
    firstName,
    lastName,
    knownAs: '',
    keeper,
  };
};

const SelectSinglePlayer: React.FunctionComponent<Props> = ({ value, label, exclude, team, onChange, add, displayNumber, filterOptionsOnTeam, keeper }) => {
  const [open, toggleOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const { data, loading, error, append } = useReferenceList('players', referencesService.getPlayers);
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = () => {
    setDialogValue({
      firstName: '',
      lastName: '',
      knownAs: '',
      keeper: false,
    });
    toggleOpen(false);
  };

  const [dialogValue, setDialogValue] = useState({
    firstName: '',
    lastName: '',
    knownAs: '',
    keeper: false,
  });

  const sortedData = useMemo(() => {
    return (
      data
        ?.slice()
        .filter((p) => p.keeper === keeper && (!filterOptionsOnTeam || p.lastKnownTeam?.id === team))
        .sort(playerSort(team)) || []
    );
  }, [data, team, keeper, filterOptionsOnTeam]);

  const selected = useMemo(() => {
    return (sortedData.find((d) => d.id === value) as PlayerOptionType) || null;
  }, [sortedData, value]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    referencesService
      .addPlayer(dialogValue)
      .then((player) => {
        append(player);
        onChange(player);
        handleClose();
      })
      .catch(() => enqueueSnackbar('Failed to add player', { variant: 'error', autoHideDuration: 3000 }));
  };

  if (error) {
    enqueueSnackbar('Failed to load Players', { variant: 'error', autoHideDuration: 3000 });
  }

  if (loading) {
    return <Progress />;
  }

  return (
    <React.Fragment>
      <Autocomplete
        value={selected}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            // timeout to avoid instant validation of the dialog's form.
            if (add) {
              setTimeout(() => {
                toggleOpen(true);
                setDialogValue(dialogValueFromString(newValue, keeper));
              });
            }
          } else if (newValue && newValue.inputValue) {
            if (add) {
              toggleOpen(true);
              setDialogValue(dialogValueFromString(newValue.inputValue, keeper));
            }
          } else {
            onChange(newValue);
          }
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (params.inputValue !== '' && add) {
            filtered.push({
              id: '',
              inputValue: params.inputValue,
              firstName: 'Add new player',
              keeper,
            });
          }

          return filtered;
        }}
        id={"Player-autocomplete"+value}
        options={sortedData as PlayerOptionType[]}
        getOptionDisabled={(option) => !!exclude && exclude.includes(option.id)}
        getOptionLabel={(option) => {
          // e.g value selected with enter, right from the input
          if (typeof option === 'string') {
            return option;
          }
          if (option.inputValue) {
            return option.inputValue;
          }
          return displayName(option, displayNumber);
        }}
        groupBy={(option) => option.lastKnownTeam?.name || '-'}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        style={{ marginTop: 16, marginBottom: 8 }}
        renderOption={(option) => {
          return displayName(option as TPlayer | null, displayNumber)}
        }
        freeSolo={add || false}
        renderInput={(params) => <TextField {...params} label={label || 'Player'} variant="standard" />}
      />
      <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
        <form onSubmit={handleSubmit}>
          <DialogTitle id="form-dialog-title">Add a new Player</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              value={dialogValue.firstName}
              onChange={(event) => setDialogValue({ ...dialogValue, firstName: event.target.value })}
              label="Firstname"
              type="text"
            />
            <TextField
              autoFocus
              margin="dense"
              id="name"
              value={dialogValue.lastName}
              onChange={(event) => setDialogValue({ ...dialogValue, lastName: event.target.value })}
              label="Lastname"
              type="text"
            />
            <TextField
              autoFocus
              margin="dense"
              id="name"
              value={dialogValue.knownAs}
              onChange={(event) => setDialogValue({ ...dialogValue, knownAs: event.target.value })}
              label="Known as"
              type="text"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={dialogValue.keeper}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setDialogValue({ ...dialogValue, keeper: event.target.checked })
                  }
                  value={dialogValue.keeper}
                  color="primary"
                />
              }
              label="Keeper"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" color="primary">
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  );
};

export default SelectSinglePlayer;
